/*
    rxConnect® ePrescribing Middleware by HITORI.
    © 2025 HITORI (Melbourne, Australia). All rights reserved.
    Website: https://hitori-inc.com

    This software is proprietary and confidential.
    Unauthorised copying, distribution, or modification of this file, via any medium, is strictly prohibited.

    Styling and theming by licensed third parties is permitted under applicable agreements.
*/

import React, { useState, useEffect } from "react";
import apiService from "./apiService";
import "./App.css";
import BarcodeIcon from "./assets/barcode-icon.png";

//use below instead of above imports for use in simple html

{
      /* <script type="text/babel">
    const { useState, useEffect } = React; */
}

//  Include React, ReactDOM, and Babel via CDN
// <script src="https://unpkg.com/react@18/umd/react.development.js" crossorigin></script>
// <script src="https://unpkg.com/react-dom@18/umd/react-dom.development.js" crossorigin></script>
// <script src="https://unpkg.com/axios/dist/axios.min.js"></script>
// <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>

const DataTable = ({ patientGUID }) => {
      const [data, setData] = useState([]);
      const [expandedRow, setExpandedRow] = useState(null);
      const [loading, setLoading] = useState(true);
      const [error, setError] = useState("");
      const [inputValues, setInputValues] = useState({});
      const [hoveredBarcode, setHoveredBarcode] = useState(null);

      const fetchData = async () => {
            if (!patientGUID) return;
            setLoading(true);
            try {
                  const response = await apiService.get(`eprescriptionlists?t=${patientGUID}`);
                  setData(response);
                  setLoading(false);
            } catch (err) {
                  setError("Error fetching data");
                  setLoading(false);
            }
      };

      // On mount or when patientGUID changes
      useEffect(() => {
            fetchData();
      }, [patientGUID]);

      // Listen for REFRESH_DATA from parent
      useEffect(() => {
            const handleMessage = (event) => {
            if (event.data?.type === "REFRESH_DATA") {
                  console.log("REFRESH_DATA received from parent");
                  fetchData();
            }
      };

      window.addEventListener("message", handleMessage);
      return () => window.removeEventListener("message", handleMessage);
      }, [patientGUID]);

      const handleSaveChanges = async (index) => {
      const updatedRow = {
            ...data[index],
            number_of_repeats_authorised: inputValues[index]?.repeats,
            repeat_intervals: inputValues[index]?.interval,
            dosing: inputValues[index]?.dosing,
            quantity: inputValues[index]?.quantity,
      };

      try {
            await apiService.patch(`eprescriptionlists/${updatedRow.id}?t=${patientGUID}`, {
            number_of_repeats_authorised: updatedRow.number_of_repeats_authorised,
            repeat_intervals: parseInt(updatedRow.repeat_intervals),
            patient_instructions: updatedRow.dosing,
            quantity: updatedRow.quantity,
            });

            const updatedData = [...data];
            updatedData[index] = updatedRow;
            setData(updatedData);
            setExpandedRow(null);
      } catch (error) {
            console.error("Failed to update the data:", error);
            alert("Error saving changes. Please try again.");
      }
      };

      const handleRowClick = (index) => {
            if (expandedRow === index) {
                  setExpandedRow(null);
            } else {
                  setExpandedRow(index);
                  if (!inputValues[index]) {
                        setInputValues((prev) => ({
                              ...prev,
                              [index]: {
                                    repeats: data[index].number_of_repeats_authorised || "",
                                    interval: data[index].repeat_intervals || "",
                                    dosing: data[index].patient_instructions,
                                    quantity: data[index].quantity,
                              },
                        }));
                  }
            }
      };

      const getStatusColor = (state) => {
            switch (state) {
                  case "Active":
                        return "#a9bb8b";
                  case "Cancelled":
                        return "red";
                  case "Cease":
                        return "orange";
                  case "Re-Issue":
                        return "gray";
                  default:
                        return "#a9bb8b";
            }
      };

      const handlePrescription = async (item, status) => {
            try {
                  await apiService.post(`eprescriptions/${item.scid}/${status}/`);
                  setExpandedRow(null);
            } catch (error) {
                  console.error("Failed to update the data:", error);
            }
      };

      const formatDate = (inputDate) => {
            const date = new Date(inputDate);
            const day = String(date.getDate()).padStart(2, "0");
            const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const month = monthNames[date.getMonth()];
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, "0");
            const minutes = String(date.getMinutes()).padStart(2, "0");
            const seconds = String(date.getSeconds()).padStart(2, "0");
            return `${day}-${month}-${year} ${hours}:${minutes}`;
      };

      const handleInputChange = (index, field, value) => {
            setInputValues((prev) => ({
                  ...prev,
                  [index]: { ...prev[index], [field]: value },
            }));
      };

      const handleNumInputChange = (index, field, value) => {
            setInputValues((prev) => {
                  const sanitizedValue = sanitizeNonNegativeInteger(value);

                  return {
                        ...prev,
                        [index]: { ...prev[index], [field]: sanitizedValue },
                  };
            });
      };

      const sanitizeNonNegativeInteger = (val) => {
            const intVal = parseInt(val, 10);

            if (val === "" || isNaN(intVal) || intVal < 0) {
                  return 0;
            }

            return intVal;
      };

      if (loading) return <div>Loading...</div>;
      if (error) return <div>{error}</div>;

      return (
            <div className="data-table">
                  <table>
                        <thead>
                              <tr>
                                    <th>RX No</th>
                                    <th>Name</th>
                                    <th></th>
                                    <th>Status</th>
                                    <th>Form</th>
                                    <th>Qty</th>
                                    <th>Rpt</th>
                                    <th>Int.</th>
                                    <th>Ordered</th>
                              </tr>
                        </thead>
                        <tbody>
                              {data.map((item, index) => (
                                    <React.Fragment key={index}>
                                          <tr className="table-row" onClick={() => handleRowClick(index)}>
                                                <td>
                                                      <span style={{ textDecoration: "underline", fontWeight: "bold" }}>
                                                            {" "}
                                                            {item.scid}{" "}
                                                      </span>
                                                      <br />
                                                      {item.created_date && formatDate(item.created_date)}
                                                </td>
                                                <td>
                                                      {item.item_trade_name} {"(" + item.item_generic_name + ")"}{" "}
                                                </td>
                                                <td>
                                                      <button
                                                            className="erx-button erx023"
                                                            onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  handlePrescription(item, "erx023");
                                                            }}
                                                      >
                                                            CANCEL
                                                      </button>
                                                      <button
                                                            className="erx-button erx061"
                                                            onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  handlePrescription(item, "erx061");
                                                            }}
                                                      >
                                                            CEASE
                                                      </button>
                                                      <button
                                                            className="erx-button erx065"
                                                            onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  handlePrescription(item, "erx065");
                                                            }}
                                                      >
                                                            RE-ISSUE
                                                      </button>
                                                </td>
                                                <td>
                                                      <div
                                                            className="item-status status-td"
                                                            style={{
                                                                  backgroundColor: getStatusColor(item.state),
                                                            }}
                                                      >
                                                            {item.state}
                                                      </div>
                                                </td>
                                                <td>{item.item_form}</td>
                                                <td>{item.quantity}</td>
                                                <td>{item.number_of_repeats_authorised}</td>
                                                <td>{item.repeat_intervals}</td>
                                                <td>{item.ordered}</td>
                                          </tr>

                                          {expandedRow === index && (
                                                <tr className="expanded-row">
                                                      <td colSpan="9">
                                                            <div className="expanded-content">
                                                                  <div className="details">
                                                                        <div className="left">
                                                                              <div className="top-layer">
                                                                                    <div className="item-status-section">
                                                                                          <strong>Status:</strong>{" "}
                                                                                          <div
                                                                                                className="item-status"
                                                                                                style={{
                                                                                                      backgroundColor:
                                                                                                            getStatusColor(
                                                                                                                  item.state
                                                                                                            ),
                                                                                                }}
                                                                                          >
                                                                                                {item.state}
                                                                                          </div>
                                                                                    </div>

                                                                                    <div className="repeats-section">
                                                                                          <strong>Repeats:</strong>
                                                                                          <br />
                                                                                          <input
                                                                                                type="number"
                                                                                                min="0"
                                                                                                step="1"
                                                                                                className="repeats-input"
                                                                                                value={inputValues[index] ?.repeats ?? 0 }
                                                                                                onChange={(e) =>
                                                                                                      handleNumInputChange(
                                                                                                            index,
                                                                                                            "repeats",
                                                                                                            e.target.value
                                                                                                      )
                                                                                                }
                                                                                          />
                                                                                    </div>
                                                                                    <div className="interval-section">
                                                                                          <strong>Interval:</strong>
                                                                                          <br />
                                                                                          <input
                                                                                                type="number"
                                                                                                min="0"
                                                                                                step="1"
                                                                                                className="repeats-input"
                                                                                                value={inputValues[index] ?.interval ?? 0 }
                                                                                                onChange={(e) =>
                                                                                                      handleNumInputChange(
                                                                                                            index,
                                                                                                            "interval",
                                                                                                            e.target.value
                                                                                                      )
                                                                                                }
                                                                                          />
                                                                                    </div>
                                                                                    <div className="interval-section">
                                                                                          <strong>Quantity:</strong>
                                                                                          <br />
                                                                                          <input
                                                                                                type="number"
                                                                                                min="0"
                                                                                                step="1"
                                                                                                className="repeats-input"
                                                                                                value={inputValues[index] ?.quantity ?? 0 }
                                                                                                onChange={(e) =>
                                                                                                      handleNumInputChange(
                                                                                                            index,
                                                                                                            "quantity",
                                                                                                            e.target.value
                                                                                                      )
                                                                                                }
                                                                                          />
                                                                                    </div>
                                                                              </div>

                                                                              {/* <div className="download-missing-annotation-buttons">
                                                                                    <button className="download-button">
                                                                                          <img
                                                                                                className="download-icon"
                                                                                                src={DownloadIcon}
                                                                                                alt="Download"
                                                                                          />
                                                                                          Download
                                                                                    </button>
                                                                                    <button className="missing-annotation-button">
                                                                                          MISSING ANNOTATION
                                                                                    </button>
                                                                              </div> */}
                                                                              <strong>Dosing:</strong>
                                                                              <br />
                                                                              <textarea
                                                                                    value={
                                                                                          inputValues[index]?.dosing ||
                                                                                          ""
                                                                                    }
                                                                                    className="text-area-repeats"
                                                                                    onChange={(e) =>
                                                                                          handleInputChange(
                                                                                                index,
                                                                                                "dosing",
                                                                                                e.target.value
                                                                                          )
                                                                                    }
                                                                              />
                                                                              <br />
                                                                              <div className="actions">
                                                                                    <button
                                                                                          className="btn black"
                                                                                          onClick={() =>
                                                                                                handleSaveChanges(index)
                                                                                          }
                                                                                    >
                                                                                          Save Changes
                                                                                    </button>
                                                                                    <button
                                                                                          className="btn white"
                                                                                          onClick={() =>
                                                                                                setExpandedRow(null)
                                                                                          }
                                                                                    >
                                                                                          Close
                                                                                    </button>
                                                                                    {/* <button className="btn orange">
                                                                                          erx027
                                                                                    </button> */}
                                                                              </div>
                                                                        </div>
                                                                        <div className="right">
                                                                              <strong>Prescribing Doctor:</strong>{" "}
                                                                              {item.doctor_first_name}{" "}
                                                                              {item.doctor_family_name}
                                                                              <br />
                                                                              <strong>Strength:</strong>{" "}
                                                                              {item.item_strength} <br />
                                                                              <strong>PBS Code:</strong> {item.pbs_code}{" "}
                                                                              <br />
                                                                              <span
                                                                                    className="barcode-text"
                                                                                    onClick={(e) =>
                                                                                          setHoveredBarcode({
                                                                                                url: item.barcode_image,
                                                                                                x: e.clientX,
                                                                                                y: e.clientY,
                                                                                          })
                                                                                    }
                                                                                    onMouseLeave={() =>
                                                                                          setHoveredBarcode(null)
                                                                                    }
                                                                              >
                                                                                    <strong>Item Barcode:</strong>{" "}
                                                                                    {item.item_barcode}{" "}
                                                                                    <img
                                                                                          className="barcode-icon"
                                                                                          src={BarcodeIcon}
                                                                                          alt="Download"
                                                                                    />{" "}
                                                                              </span>
                                                                              <strong>Schedule Number:</strong>{" "}
                                                                              {item.schedule_number} <br />
                                                                              <strong>
                                                                                    Prescriber Script Number:
                                                                              </strong>{" "}
                                                                              {item.prescriber_script_number} <br />
                                                                              <strong>
                                                                                    Controlled Substance Reference:
                                                                              </strong>{" "}
                                                                              {item.controlled_substance_reference}{" "}
                                                                              <br />
                                                                        </div>
                                                                  </div>
                                                            </div>
                                                      </td>
                                                </tr>
                                          )}
                                    </React.Fragment>
                              ))}
                        </tbody>
                  </table>
                  {hoveredBarcode && (
                        <div
                              className="barcode-popup"
                              style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    backgroundColor: "white",
                                    padding: "10px",
                                    borderRadius: "8px",
                                    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                                    zIndex: 1000,
                              }}
                        >
                              <img src={hoveredBarcode.url} alt="Barcode" style={{ width: "500px", height: "auto" }} />
                        </div>
                  )}
            </div>
      );
};

//delete this one
export default DataTable;

//and

// Render the DataTable component into the root element.
// This code can be reused in a React application by simply
// importing DataTable and using <DataTable /> in your JSX.
// ReactDOM.createRoot(document.getElementById('root')).render(<DataTable />);
